import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Container,
  Divider,
} from '@mui/material';
import { CheckCircle, Description, Visibility } from '@mui/icons-material';
import { completeValidate, getValidateActionDetails, ValidateActionDetails, CompleteValidateResponse, ValidateApiError, ValidateReferenceDocument } from '../../services/validateApi';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  dismissAfterActionForm,
  EXTERNAL_ACTION_FORM_COMPLETION_MESSAGE,
  shouldNavigateIntoPortfolioAfterAction,
} from '../../utils/actionFormNavigation';
import { apiService } from '../../services/api';
import { shouldOpenSharePointDirectly } from '../../utils/sharePointLinkUtils';
import { resolveDownloadFileName } from '../../utils/downloadFileName';
import { ActionComments } from '../ProjectExecution/ActionComments';

interface ValidateActionFormProps {
  token: string | null;
  actionId?: number | null;
  projectId?: number | null;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export default function ValidateActionForm({ token, actionId, projectId, onSuccess, onError }: ValidateActionFormProps) {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [response, setResponse] = useState<CompleteValidateResponse | null>(null);
  const [actionDetails, setActionDetails] = useState<ValidateActionDetails | null>(null);

  useEffect(() => {
    if (!token && actionId && !isAuthenticated) {
      setError('You must be logged in to complete validate actions from the execution view. Please log in and try again.');
    }
  }, [token, actionId, isAuthenticated]);

  useEffect(() => {
    const fetchDetails = async () => {
      setLoadingDetails(true);
      try {
        const details = await getValidateActionDetails(token, actionId || undefined);
        setActionDetails(details);
      } catch (_err) {
        if (actionId) {
          setActionDetails({ actionId, actionType: 'VALIDATE', referenceDocuments: [] });
        } else {
          setActionDetails({ actionId: 0, actionType: 'VALIDATE', referenceDocuments: [] });
        }
      }
      setLoadingDetails(false);
    };
    if (token || actionId) {
      fetchDetails();
    } else {
      setLoadingDetails(false);
    }
  }, [token, actionId]);

  const handleSubmit = async () => {
    if (comment.length > 2000) {
      setError('Comment cannot exceed 2000 characters');
      return;
    }
    if (!token && !actionId) {
      setError('Invalid request. Token or actionId is required.');
      return;
    }
    if (!token && actionId && !isAuthenticated) {
      setError('You must be logged in to complete validate actions. Please refresh the page and log in.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const result = await completeValidate(token, { comment: comment.trim() || undefined }, actionId || undefined);
      setResponse(result);
      setSuccess(true);
      if (onSuccess) {
        setTimeout(() => onSuccess(), 2000);
      }
    } catch (err) {
      const apiError = err as ValidateApiError;
      const errorMessage = apiError.message || 'Failed to complete validate action. Please try again.';
      setError(errorMessage);
      if (onError) onError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const openReferenceDoc = async (doc: ValidateReferenceDocument) => {
    if (shouldOpenSharePointDirectly(user) && doc.webUrl) {
      window.open(doc.webUrl, '_blank');
      return;
    }
    if (!doc.spItemId) {
      setError('This reference document is not available yet.');
      return;
    }
    const nameOpts = { fileName: doc.fileName, documentTitle: doc.documentTitle };
    try {
      const { blob, fileName } = await apiService.viewFileWithInfo(doc.spItemId, 'SHAREPOINT');
      apiService.triggerBlobDownload(blob, resolveDownloadFileName({ headerFileName: fileName, blob, ...nameOpts }));
    } catch (_err) {
      try {
        const { blob, fileName } = await apiService.downloadFileWithInfo(doc.spItemId, 'SHAREPOINT');
        apiService.triggerBlobDownload(blob, resolveDownloadFileName({ headerFileName: fileName, blob, ...nameOpts }));
      } catch (downloadErr) {
        console.error('Failed to open reference document:', downloadErr);
        setError('Failed to open reference document. Please try again.');
      }
    }
  };

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Validation Completed
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Thank you. Your confirmation has been recorded.
          </Typography>
          {response?.comment && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1, textAlign: 'left' }}>
              <Typography variant="subtitle2" gutterBottom>Your Comment:</Typography>
              <Typography variant="body2">{response.comment}</Typography>
            </Box>
          )}
          {!shouldNavigateIntoPortfolioAfterAction(user) ? (
            <Typography variant="body1" sx={{ mt: 2, fontWeight: 500 }}>
              {EXTERNAL_ACTION_FORM_COMPLETION_MESSAGE}
            </Typography>
          ) : (
            <Button
              variant="contained"
              onClick={() => {
                dismissAfterActionForm(navigate, user, {
                  internalProjectId: projectId ?? undefined,
                });
              }}
              sx={{ mt: 2 }}
            >
              {projectId ? 'Return to Execution View' : 'Return to Dashboard'}
            </Button>
          )}
        </Paper>
      </Container>
    );
  }

  const referenceDocuments = actionDetails?.referenceDocuments || [];

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Validate / Complete
        </Typography>
        <Divider sx={{ my: 3 }} />

        {loadingDetails ? (
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">Loading action details...</Typography>
          </Box>
        ) : actionDetails ? (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            {actionDetails.projectName && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                <strong>Project:</strong> {actionDetails.projectName}
              </Typography>
            )}
            {actionDetails.phaseName && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                <strong>Phase:</strong> {actionDetails.phaseName}
              </Typography>
            )}
            {actionDetails.taskName && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                <strong>Task:</strong> {actionDetails.taskName}
              </Typography>
            )}
            {actionDetails.name && (
              <Typography variant="body2" color="text.secondary">
                <strong>Action:</strong> {actionDetails.name}
              </Typography>
            )}
          </Box>
        ) : null}

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>Instructions</Typography>
          <Typography variant="body2" color="text.secondary">
            {actionDetails?.instructions
              || 'Confirm that the specified activity, condition, or milestone has occurred. Completing this action is an affirmative confirmation. Comments are optional.'}
          </Typography>
        </Box>

        {referenceDocuments.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Reference documents (informational)
            </Typography>
            {referenceDocuments.map((doc) => (
              <Box key={doc.actionDocumentId} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 1 }}>
                <Description sx={{ color: 'text.secondary' }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" fontWeight="medium">
                    {doc.fileName || doc.documentTitle || 'Document'}
                  </Typography>
                  {doc.documentDescription && (
                    <Typography variant="caption" color="text.secondary">{doc.documentDescription}</Typography>
                  )}
                </Box>
                {(doc.spItemId || doc.webUrl) && (
                  <Button variant="outlined" size="small" startIcon={<Visibility />} onClick={() => openReferenceDoc(doc)}>
                    View
                  </Button>
                )}
              </Box>
            ))}
          </Box>
        )}

        {actionId ? <ActionComments actionId={actionId} title="Previous comments" /> : null}

        <TextField
          label="Comment (optional)"
          multiline
          minRows={3}
          fullWidth
          value={comment}
          onChange={(e) => {
            if (e.target.value.length <= 2000) {
              setComment(e.target.value);
              setError(null);
            }
          }}
          helperText={`${comment.length}/2000`}
          sx={{ mb: 2 }}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        <Button
          variant="contained"
          color="primary"
          fullWidth
          disabled={loading || (!token && !isAuthenticated)}
          onClick={handleSubmit}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Validate / Complete'}
        </Button>
      </Paper>
    </Container>
  );
}
